import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Trash2, X, ArrowRight, Bot } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const sessionKey = (uid) => `ai_assistant_session_${uid}`;
const newSession = (uid) => { const id = `as-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`; localStorage.setItem(sessionKey(uid), id); return id; };
const getSession = (uid) => localStorage.getItem(sessionKey(uid)) || newSession(uid);

// the model occasionally slips in **bold** — render it instead of showing asterisks
const rich = (t) => String(t || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part));

const SUGGEST = {
  admin: ["How did we do today by station?", "How much cash is in the safe?", "Who owes us the most?", "What is low on stock?"],
  cashier: ["How is my shift going?", "How much cash should be in my drawer?", "What did I sell today?"],
};

/** Floating "Ask my store" assistant — every page, every role (cashiers only see their own shift). */
export const AskStoreWidget = () => {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => (user ? getSession(user.id) : "default"));
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const isAdmin = ["tenant_owner", "manager"].includes(user?.role_code);

  useEffect(() => {
    if (!user || !tenant) return;
    fetch(`${BACKEND}/api/tenant/ai/settings`, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setCfg(d)).catch(() => setCfg(null));
  }, [user, tenant]);

  const loadHistory = useCallback((sid) => {
    fetch(`${BACKEND}/api/tenant/ai/assistant/history?session_id=${sid}`, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages((d.messages || []).map((m, i) => ({ id: `h${i}`, role: m.role, content: m.content, actions: m.actions || [] }))))
      .catch(() => {});
  }, []);
  useEffect(() => { if (open) loadHistory(sessionId); }, [open, sessionId, loadHistory]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open]);
  useEffect(() => () => abortRef.current?.abort(), []);

  if (!user || !tenant || !cfg?.can_use || location.pathname.startsWith("/login")) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", content: msg }, { id: `a${Date.now()}`, role: "assistant", content: "", actions: [] }]);
    const patchLast = (fn) => setMessages((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      abortRef.current = new AbortController();
      const res = await fetch(`${BACKEND}/api/tenant/ai/assistant`, {
        method: "POST", credentials: "include", signal: abortRef.current.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (res.status === 429) throw new Error("Too many questions — give it a minute");
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
          if (!chunk.startsWith("data: ")) continue;
          let d; try { d = JSON.parse(chunk.slice(6)); } catch { continue; }
          if (d.delta) patchLast((l) => { const c = l.content + d.delta; const cut = c.indexOf("ACTIONS:"); return { ...l, content: cut >= 0 ? c.slice(0, cut) : c }; });
          if (d.error) patchLast((l) => ({ ...l, content: d.error }));
          if (d.done) patchLast((l) => ({ ...l, content: d.answer || l.content, actions: d.actions || [] }));
        }
      }
    } catch (e) {
      patchLast((l) => ({ ...l, content: e.message === "request failed" ? "The assistant is unavailable right now. Please try again." : e.message }));
    }
    setBusy(false);
  };

  const reset = () => { const sid = newSession(user.id); setSessionId(sid); setMessages([]); };
  const go = (path) => { setOpen(false); navigate(path); };
  const sugg = SUGGEST[isAdmin ? "admin" : "cashier"];

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ask-store-fab"
          className={`fixed z-40 right-4 md:right-6 h-12 w-12 rounded-full shadow-xl bg-th-accent text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform ${location.pathname === "/pos" ? "bottom-20 md:bottom-24" : "bottom-20 md:bottom-6"}`}
          title="Ask my store (AI)" aria-label="Ask my store">
          <Sparkles className="w-5 h-5" />
        </button>
      )}
      {open && (
        <div className="fixed z-50 bottom-0 right-0 md:bottom-6 md:right-6 w-full md:w-[400px] h-[80vh] md:h-[560px] bg-th-card border border-th-border md:rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="ask-store-panel">
          <div className="h-12 px-3 flex items-center justify-between border-b border-th-border bg-th-surface">
            <div className="flex items-center gap-2 text-sm font-semibold text-cream-900"><Bot className="w-4 h-4 text-th-accent" /> Ask my store <span className="text-[10px] font-normal text-th-dim">· {isAdmin ? "store-wide" : "your shift"}</span></div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && <button onClick={reset} className="p-1.5 rounded-md text-th-dim hover:text-red-500" title="New chat" data-testid="ask-store-new"><Trash2 className="w-4 h-4" /></button>}
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md text-th-dim hover:text-cream-900" data-testid="ask-store-close"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="ask-store-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-2">
                <p className="text-sm text-th-muted">{isAdmin ? "Ask anything about today's sales, shifts, cash, stock or customers." : "Ask about your current shift and your sales."}</p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {sugg.map((s) => <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full bg-th-surface text-xs text-th-muted hover:text-th-accent border border-th-border" data-testid="ask-store-suggestion">{s}</button>)}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[88%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-th-accent text-white rounded-br-md" : "bg-th-surface text-cream-900 rounded-bl-md"}`} data-testid={`ask-store-msg-${m.role}`}>
                  {m.content ? rich(m.content) : (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-th-dim" /> : "")}
                </div>
                {m.role === "assistant" && m.actions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="ask-store-actions">
                    {m.actions.map((a) => <button key={a.path + a.label} onClick={() => go(a.path)} className="text-[11px] px-2.5 py-1 rounded-full border border-th-accent/40 text-th-accent hover:bg-th-accent hover:text-white inline-flex items-center gap-1">{a.label} <ArrowRight className="w-3 h-3" /></button>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-th-border flex gap-2 bg-th-card">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={busy}
              placeholder={isAdmin ? "e.g. which station sold most today?" : "e.g. how much cash should I have?"} className="flex-1 h-10 px-3 rounded-xl border border-th-border bg-th-bg text-sm outline-none focus:border-th-accent" data-testid="ask-store-input" />
            <Button onClick={() => send()} disabled={busy || !input.trim()} className="h-10 w-10 p-0 rounded-xl" data-testid="ask-store-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
          </div>
        </div>
      )}
    </>
  );
};
